import { Box, useClassNames } from '@factorialco/deprecated-design-system'
import { Moment } from 'moment'

import EmployeeCellContent, {
  EmployeeCellContentType,
} from 'modules/project_management/components/Schedule/Body/ScheduleItem/HeaderRow/EmployeeCellContent'
import 'modules/project_management/components/Schedule/Body/ScheduleItem/index.styles.css'
import { stylesRefs } from 'modules/project_management/components/Schedule/Body/ScheduleItem/stylesRefs'

import { HeaderCellType, RowType, ScheduleHeaderRowType } from '../types'
import { getBackgroundColor, sumMinutesForDate } from '../utils'
import HeaderCell from './HeaderCell'
import HeaderOffDaysCell from './HeaderOffDaysCell'

interface IHeaderRowProps {
  days: Moment[]
  dataHeaderRow: ScheduleHeaderRowType
  rows: RowType[]
  toggle: () => void
  open: boolean
  isChevronHidden: boolean
}

const DEFAULT_GRID_ROWS = 2

const HeaderRow = ({
  days,
  dataHeaderRow,
  rows,
  toggle,
  open,
  isChevronHidden = false,
}: IHeaderRowProps) => {
  let gridRows = DEFAULT_GRID_ROWS
  const cx = useClassNames(stylesRefs)

  const generateHeaderCellsTimes = (): number[] => {
    const records =
      rows.flatMap((row) => {
        return Object.values(row.records).flat()
      }) ?? []
    return days.map((day) => sumMinutesForDate(day, records))
  }

  const generateHeaderCellsEstimatedWorkTimes = (calculatedHeaderCellsTimes: number[]) => {
    const result: {
      worktimes: HeaderCellType[]
      offdays: HeaderCellType[]
    } = { worktimes: [], offdays: [] }

    days.forEach((day, index) => {
      const estimatedWorkTimes = dataHeaderRow.estimatedWorkTimesData[day.format('YYYY-MM-DD')]

      const offdayMinutes = estimatedWorkTimes?.minutesOff || 0

      const worktimeMinutes = calculatedHeaderCellsTimes[index] || 0

      result.offdays.push({
        plannedMinutes: worktimeMinutes,
        expectedMinutes: estimatedWorkTimes?.minutes || 0,
        minutesOff: offdayMinutes,
        startDate: day,
        endDate: day,
        columnStart: index + 1,
        columnSpan: 1,
        rowStart: 1,
        rowSpan: worktimeMinutes ? 1 : 2,
      })
      result.worktimes.push({
        plannedMinutes: worktimeMinutes,
        expectedMinutes: estimatedWorkTimes?.minutes || 0,
        minutesOff: offdayMinutes,
        startDate: day,
        endDate: day,
        columnStart: index + 1,
        columnSpan: 1,
        rowStart: offdayMinutes ? 2 : 1,
        rowSpan: offdayMinutes ? 1 : 2,
      })
    })
    return result
  }

  const groupHeaderCellsOffdays = (offdays: HeaderCellType[]) => {
    let breakCell = false
    return offdays.reduce<HeaderCellType[]>((acc, current) => {
      if (current.minutesOff !== 0) {
        const lastCell = acc[acc.length - 1]
        if (
          lastCell &&
          lastCell?.plannedMinutes === current.plannedMinutes &&
          lastCell.expectedMinutes === current.expectedMinutes &&
          lastCell.minutesOff === current.minutesOff &&
          !breakCell
        ) {
          lastCell.columnSpan += 1
          lastCell.rowSpan = Math.min(lastCell.rowSpan, current.rowSpan)
          lastCell.endDate = current.endDate
        } else {
          acc.push(current)
        }
        breakCell = false
      } else {
        breakCell = true
      }
      return acc
    }, [])
  }

  const groupHeaderCellsEstimatedWorkTimes = (worktimes: HeaderCellType[]) => {
    let breakCell = false
    return worktimes.reduce<HeaderCellType[]>((acc, current) => {
      if (current.plannedMinutes !== 0) {
        const lastCell = acc[acc.length - 1]
        if (
          lastCell &&
          lastCell?.plannedMinutes === current.plannedMinutes &&
          lastCell.expectedMinutes === current.expectedMinutes &&
          lastCell.minutesOff === current.minutesOff &&
          !breakCell
        ) {
          lastCell.columnSpan += 1
          lastCell.rowSpan = Math.min(lastCell.rowSpan, current.rowSpan)
          lastCell.rowStart = Math.max(lastCell.rowStart, current.rowStart)
          lastCell.endDate = current.endDate
        } else {
          acc.push(current)
        }
        breakCell = false
      } else {
        breakCell = true
      }
      return acc
    }, [])
  }

  const headerCellsTimes = generateHeaderCellsTimes()
  const totalCells = days.length

  const plannedWorkTimes = headerCellsTimes.reduce((acc, current) => {
    return acc + current
  }, 0)

  const estimatedWorkTimes = Object.values(dataHeaderRow.estimatedWorkTimesData).reduce(
    (acc, current) => {
      return acc + current.minutes
    },
    0
  )

  const daysOffTimes = Object.values(dataHeaderRow.estimatedWorkTimesData).reduce(
    (acc, current) => {
      return acc + current.minutesOff
    },
    0
  )

  const headerCellsEstimatedWorkTimes = generateHeaderCellsEstimatedWorkTimes(headerCellsTimes)
  const groupedCellsEstimatedWorkTimes = groupHeaderCellsEstimatedWorkTimes(
    headerCellsEstimatedWorkTimes.worktimes
  )
  const formatGroupHeaderCellsEstimatedWorkTimes = (
    groupedCellsEstimatedWorkTimes: HeaderCellType[]
  ) => {
    return groupedCellsEstimatedWorkTimes.map((cell) => {
      if (cell.rowStart === 2 && cell.rowSpan === 1 && cell.columnSpan === 1) {
        gridRows = 3
        return {
          ...cell,
          rowSpan: 2,
        }
      } else {
        return cell
      }
    })
  }
  const formatedGroupedCellsEstimatedWorkTimes = formatGroupHeaderCellsEstimatedWorkTimes(
    groupedCellsEstimatedWorkTimes
  )
  const groupedCellsOffdays = groupHeaderCellsOffdays(headerCellsEstimatedWorkTimes.offdays)

  const employeeData: EmployeeCellContentType = {
    id: dataHeaderRow.id,
    fullName: dataHeaderRow.fullName,
    avatar: dataHeaderRow.avatar,
    jobTitle: dataHeaderRow.jobTitle,
    estimatedWorkTimes: estimatedWorkTimes,
    plannedWorkTimes: plannedWorkTimes,
    daysOffTimes: daysOffTimes,
  }

  return (
    <Box
      className={cx('scheduleItemRow')}
      border={
        open
          ? {
              top: {
                color: 'grey300',
                width: 's1',
                style: 'solid',
              },
            }
          : undefined
      }
    >
      <Box className={cx('scheduleItemFirstColumn')} onClick={toggle}>
        <EmployeeCellContent
          employee={employeeData}
          open={open}
          isChevronHidden={isChevronHidden}
        />
      </Box>
      <Box
        onClick={toggle}
        className={cx('scheduleItemHeaderDateContainer')}
        style={
          {
            '--schedule-item-grid-columns': totalCells.toString(),
            '--schedule-item-grid-rows': gridRows.toString(),
          } as unknown as Partial<CSSStyleDeclaration>
        }
      >
        {groupedCellsOffdays.map((cell, index) => (
          <HeaderOffDaysCell key={index} data={cell} />
        ))}

        {formatedGroupedCellsEstimatedWorkTimes.map((cell, index) => (
          <HeaderCell key={index} data={cell} />
        ))}

        <Box
          className={cx('gridOverlay')}
          style={
            {
              '--schedule-item-grid-columns': totalCells.toString(),
            } as unknown as Partial<CSSStyleDeclaration>
          }
        >
          {Array.from({ length: totalCells }).map((_, i) => (
            <Box key={i} background={getBackgroundColor(i, days)} className={cx('gridLine')} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default HeaderRow
